import React, { createContext, useContext, useState, useEffect } from "react";
import { NotificationPreferences, loadNotificationPreferences, saveNotificationPreferences } from "../utils/notificationService";

type GlobalContextType = {
  role: string;
  setRole: (value: string) => void;
  userId: string;
  setUserId: (value: string) => void;
  notificationSidebarOpen: boolean;
  setNotificationSidebarOpen: (value: boolean) => void;
  theme: 'light' | 'dark';
  setTheme: (value: 'light' | 'dark') => void;
  notificationPreferences: NotificationPreferences;
  setNotificationPreferences: (preferences: NotificationPreferences) => void;
};

const GlobalContext = createContext<GlobalContextType | undefined>(undefined);

export const GlobalProvider = ({ children }: { children: React.ReactNode }) => {
  const [role, setRole] = useState(sessionStorage.getItem("role") || "");
  const [userId, setUserId] = useState(sessionStorage.getItem("user_id") || "");
  const [notificationSidebarOpen, setNotificationSidebarOpen] = useState(false);
  const [theme, setTheme] = useState<'light' | 'dark'>(
    (sessionStorage.getItem("theme") as 'light' | 'dark') || 'light'
  );
  const [notificationPreferences, setNotificationPreferencesState] = useState<NotificationPreferences>(
    loadNotificationPreferences()
  );

  // Keep sessionStorage in sync when role/userId changes
  useEffect(() => {
    sessionStorage.setItem("role", role);
    sessionStorage.setItem("user_id", userId);
  }, [role, userId]);

  // Apply theme to document root
  useEffect(() => {
    const root = document.documentElement;
    if (theme === 'dark') {
      root.classList.add('dark');
    } else {
      root.classList.remove('dark');
    }
    sessionStorage.setItem("theme", theme);
  }, [theme]);

  // Save notification preferences when they change
  const setNotificationPreferences = (preferences: NotificationPreferences) => {
    setNotificationPreferencesState(preferences);
    saveNotificationPreferences(preferences);
  };

  return (
    <GlobalContext.Provider value={{ 
      role, 
      setRole, 
      userId, 
      setUserId, 
      notificationSidebarOpen, 
      setNotificationSidebarOpen, 
      theme, 
      setTheme,
      notificationPreferences,
      setNotificationPreferences
    }}>
      {children}
    </GlobalContext.Provider>
  );
};

export const useGlobal = () => {
  const context = useContext(GlobalContext);
  if (!context) {
    throw new Error("useGlobal must be used within a GlobalProvider");
  }
  return context;
};
